import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, Check, Flag, ListOrdered } from 'lucide-react';
import { useShortcuts, type Shortcut } from '~/lib/shortcuts';
import { autoComplete, setMark, touchStep, usePathProgress } from '~/lib/path-progress';
import { createReadTracker, dwellTarget } from '~/lib/read-tracker';
import type { PathContext } from '#shared/types/public';

/* De vaste balk onderaan een artikel dat je als stap van een leerpad leest.
   Hier zit de hele beweging "lees – afvinken – volgende" in één rij.

   DRIE DINGEN DIE BEWUST ZO ZIJN:

   1. HIJ VERBERGT ZICH NIET BIJ SCROLLEN, anders dan de leesbalk hiernaast.
      De klacht die dit alles startte was dat de knop niet te vinden was; een
      knop die wegduikt zodra je hem zoekt, lost dat niet op.

   2. HIJ MEET ZICHZELF en zet --kb-stepbar-h op <html>. De leesbalk en de
      chatknop zweven op precies dezelfde plek onderaan; die schuiven met die
      variabele omhoog in plaats van dat hier drie breekpunten worden
      nagebouwd. Bij het opruimen gaat de variabele terug naar 0px.

   3. AUTOMATISCH AFVINKEN WOONT HIER, niet in de pagina — want de bevestiging
      ("Stap 3 afgevinkt · Ongedaan maken") hoort in dezelfde balk te staan als
      het vinkje dat verandert. Die bevestiging verdwijnt NIET vanzelf na een
      paar tellen: een ongedaan-maken dat wegloopt terwijl je hem leest, is
      voor toetsenbord- en schermlezergebruikers geen ongedaan maken. */

export function PathStepBar({
  ctx, articleId, readingMinutes, sentinelRef, hidden = false,
}: {
  ctx: PathContext;
  articleId: string;
  readingMinutes: number;
  /** Leeg element direct ná de artikeltekst — het sein "einde bereikt". */
  sentinelRef: React.RefObject<HTMLElement | null>;
  /** In volledig scherm is de balk uit beeld; dan ook geen ruimte reserveren. */
  hidden?: boolean;
}) {
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { markOf, isDone, ready } = usePathProgress(ctx.id);
  const [justTicked, setJustTicked] = useState(false);

  const mark = markOf(articleId);
  const done = ready && Boolean(mark);
  const href = useCallback(
    (slug: string) => `/artikel/${encodeURIComponent(slug)}?pad=${encodeURIComponent(ctx.slug)}`,
    [ctx.slug],
  );
  const pathHref = `/leerpad/${encodeURIComponent(ctx.slug)}#stap-${ctx.index + 1}`;

  /* De hoogte doorgeven aan wie er ook onderaan zweeft. */
  useEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (hidden || !el) {
      root.style.setProperty('--kb-stepbar-h', '0px');
      return;
    }
    const set = () => root.style.setProperty('--kb-stepbar-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--kb-stepbar-h', '0px');
    };
  }, [hidden]);

  /* Hervattingswijzer: hier ben je gebleven. */
  useEffect(() => { touchStep(ctx.id, articleId); }, [ctx.id, articleId]);

  /* Automatisch afvinken. Draait alleen zolang de stap nog nergens op staat —
     zodra hij gemarkeerd is (door ons, door jou, of door een ander tabblad)
     ruimt dit effect zichzelf op. */
  useEffect(() => {
    if (!ready || mark !== undefined) return;
    const el = sentinelRef.current;
    if (!el) return;
    const tracker = createReadTracker({
      sentinel: el,
      required: dwellTarget(readingMinutes),
      onRead: () => { if (autoComplete(ctx.id, articleId)) setJustTicked(true); },
    });
    return () => tracker.stop();
  }, [ready, mark, ctx.id, articleId, readingMinutes, sentinelRef]);

  const toggle = useCallback(() => {
    setJustTicked(false);
    setMark(ctx.id, articleId, isDone(articleId) ? 0 : 1);
  }, [ctx.id, articleId, isDone]);

  /** Ongedaan maken zet 0 en niet "afwezig": dat is een weigering, en zonder
   *  dat verschil vinkt de automaat de stap een halve minuut later opnieuw af. */
  const undo = useCallback(() => {
    setMark(ctx.id, articleId, 0);
    setJustTicked(false);
  }, [ctx.id, articleId]);

  const goNext = useCallback(() => {
    if (!isDone(articleId)) setMark(ctx.id, articleId, 1);
    nav(ctx.next ? href(ctx.next.slug) : pathHref, { viewTransition: true });
  }, [ctx, articleId, isDone, nav, href, pathHref]);

  const goPrev = useCallback(() => {
    nav(ctx.prev ? href(ctx.prev.slug) : pathHref, { viewTransition: true });
  }, [ctx, nav, href, pathHref]);

  const plain = (k: string) => (e: KeyboardEvent) =>
    e.key === k && !e.ctrlKey && !e.metaKey && !e.altKey;
  useShortcuts(useMemo<Shortcut[]>(() => [
    { keys: 'n', description: 'Volgende stap', match: plain('n'), run: goNext },
    { keys: 'p', description: 'Vorige stap', match: plain('p'), run: goPrev },
    { keys: 'l', description: 'Terug naar het leerpad', match: plain('l'), run: () => nav(pathHref) },
  ], [goNext, goPrev, nav, pathHref]));

  if (hidden) return null;

  const doneCount = ready ? ctx.steps.filter((s) => isDone(s.articleId)).length : 0;
  const pct = ctx.total ? Math.round((doneCount / ctx.total) * 100) : 0;

  return (
    <div
      ref={ref}
      className="kb-no-print fixed inset-x-0 bottom-0 z-160 border-t border-hairline bg-raised/95 shadow-lg backdrop-blur-sm"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Voortgang als lijn op de bovenrand: kost geen hoogte in een balk die
          op een telefoon toch al schaars is. */}
      <div className="h-0.5 w-full bg-sunken" aria-hidden>
        <div
          className="h-full bg-gradient-to-r from-accent-logo to-orange-400 motion-safe:transition-[width] motion-safe:duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {justTicked && (
        <div
          role="status"
          aria-live="polite"
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-hairline bg-success-soft px-4 py-2 text-base motion-safe:animate-fade-up"
        >
          <span className="flex items-center gap-2 text-ink">
            <Check size={16} className="shrink-0 text-success" aria-hidden />
            Stap {ctx.index + 1} afgevinkt — je las hem uit.
          </span>
          <button
            type="button"
            onClick={undo}
            className="shrink-0 rounded-md px-2 py-0.5 font-semibold text-ink underline underline-offset-2 transition hover:bg-surface-hover"
          >
            Ongedaan maken
          </button>
        </div>
      )}

      <nav
        aria-label="Leerpadnavigatie"
        className="mx-auto flex max-w-[1180px] items-center gap-2 px-3 py-2 sm:gap-3 sm:px-5"
      >
        {ctx.prev ? (
          <Link
            viewTransition
            to={href(ctx.prev.slug)}
            title={`Vorige stap: ${ctx.prev.name} (p)`}
            className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-md px-2 text-base text-ink-2 transition hover:bg-surface-hover hover:text-ink sm:px-3"
          >
            <ArrowLeft size={17} aria-hidden />
            <span className="hidden sm:inline">Vorige</span>
            <span className="sr-only sm:hidden">Vorige stap</span>
          </Link>
        ) : (
          <span className="hidden sm:block sm:w-2" aria-hidden />
        )}

        <Link
          viewTransition
          to={pathHref}
          title={`Terug naar leerpad ${ctx.title} (l)`}
          className="flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 transition hover:bg-surface-hover hover:text-ink"
        >
          <ListOrdered size={18} aria-hidden />
          <span className="sr-only">Terug naar leerpad {ctx.title}</span>
        </Link>

        <p className="min-w-0 flex-1 leading-tight">
          <span className="block text-sm text-ink-3 tabular-nums">
            Stap {ctx.index + 1} van {ctx.total}
            {ready && doneCount > 0 && <span className="hidden sm:inline"> · {doneCount} klaar</span>}
          </span>
          <span className="block truncate text-sm font-medium text-ink sm:text-base">{ctx.title}</span>
        </p>

        <button
          type="button"
          onClick={toggle}
          aria-pressed={done}
          aria-label={done
            ? `Stap ${ctx.index + 1} als niet afgerond markeren`
            : `Stap ${ctx.index + 1} als afgerond markeren`}
          className={`flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border px-3 text-sm transition ${
            done
              ? 'border-success/50 bg-success-soft text-success'
              : 'border-hairline-strong text-ink-2 hover:border-accent hover:text-ink'
          }`}
        >
          <Check size={16} aria-hidden />
          <span className="hidden sm:inline">{done ? 'Afgerond' : 'Klaar'}</span>
        </button>

        <button
          type="button"
          onClick={goNext}
          title={ctx.next ? `Volgende stap: ${ctx.next.name} (n)` : 'Leerpad afronden (n)'}
          className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-md bg-accent px-3 text-base font-semibold text-accent-on shadow-sm transition hover:bg-accent-hover hover:shadow-glow sm:px-4"
        >
          {ctx.next ? (
            <>
              <span className="hidden sm:inline">{done ? 'Volgende stap' : 'Klaar & volgende'}</span>
              <span className="sm:hidden">Volgende</span>
              <ArrowRight size={17} aria-hidden />
            </>
          ) : (
            <>
              <Flag size={16} aria-hidden />
              <span className="hidden sm:inline">Leerpad afronden</span>
              <span className="sm:hidden">Afronden</span>
            </>
          )}
        </button>
      </nav>
    </div>
  );
}
